import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import { observer } from "mobx-react-lite";
import React, { useCallback, useImperativeHandle, useRef, useState, useEffect } from "react";
import styles from "./CollapsibleMenu.module.scss";
import { useClickOutside } from "@whatslly/ui-components/componentsUtils";
import classnames from "classnames";

export const CollapsibleMenu = observer(
  React.forwardRef<
    { close: () => void },
    {
      className?: string;
      menuClassName?: string;
      button: React.FC<{ isOpen: boolean }>;
      title?: React.ReactNode;
      children?: React.ReactNode;
      disabled?: boolean;
      ignoreCloseOnClickOutside?: boolean;
      onClose?: () => void;
      onOpen?: () => void;
      testId?: string;
    }
  >(({ className, menuClassName, title, children, button: Button, disabled, ignoreCloseOnClickOutside, onClose, onOpen, testId }, ref) => {
    const buttonRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const clickOutSideRef = useClickOutside<HTMLDivElement>({
      callback: () => !ignoreCloseOnClickOutside && setIsOpen(false),
      activate: true,
      ignoredElements: [buttonRef],
    });

    const onClickButton = useCallback(
      (event: React.MouseEvent) => {
        if (disabled) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(!isOpen);
      },
      [disabled, isOpen]
    );

    useImperativeHandle(ref, () => ({ close: () => setIsOpen(false) }));

    useEffect(() => {
      if (disabled && isOpen) {
        setIsOpen(false);
      }
      isOpen ? onOpen?.() : onClose?.();
    }, [disabled, isOpen]);
    return (
      <div className={classnames(styles.collapsibleMenu, className)} ref={clickOutSideRef}>
        <div onClick={onClickButton} ref={buttonRef}>
          <Button isOpen={isOpen} />
        </div>
        {isOpen && (
          <div className={classnames(styles.container, menuClassName)} data-testid={testId}>
            {title && (
              <div className={styles.header}>
                <div>{title}</div>
                <Svg iconName={`cancel-actions`} onClick={() => setIsOpen(false)} />
              </div>
            )}
            {children}
          </div>
        )}
      </div>
    );
  })
);
